import React, { useCallback, useContext } from 'react';
import { DataRequestArgs, RestArgs, useDataRequest } from './useDataRequest';
import * as storage from '../services/storage';
import { useDataReturn } from '../types';

export interface AuthTokens {
	token: string;
	refreshToken: string;
}

export function useAuthToken() {
	const set = useCallback((tokens: AuthTokens) => {
		const { token, refreshToken } = tokens;
		storage.set('token', token);
		storage.set('refreshToken', refreshToken);
	}, []);

	const get = useCallback((): AuthTokens | null => {
		if (!storage.get('token')) return null;
		return {
			token: storage.get('token') || '',
			refreshToken: storage.get('refreshToken') || '',
		};
	}, []);

	const forget = useCallback(() => {
		storage.remove('token');
		storage.remove('refreshToken');
	}, []);

	return {
		set,
		get,
		forget,
	};
}

export interface LoginDtp {
	username: string;
	password: string;
	deviceId: string;
	deviceModelName: string;
	deviceOS: string;
}

type ACTIONS = 'login' | 'refreshToken' | 'forgot-pw' | 'reset-pw' | 'logout' | 'change-pw';

interface AuthArgs extends DataRequestArgs {
	action: ACTIONS;
	paths?: Record<string, string>;
}

export const AUTH_PATHS = {
	tokens: 'api/{ver}/tokens',
	refreshToken: 'api/{ver}/tokens/refresh',
	changePassword: 'api/{ver}/personal/change-password',
	forgotPassword: 'api/{ver}/users/forgot-password',
	resetPassword: 'api/{ver}/users/reset-password',
	logout: 'api/{ver}/personal/logout',
};

export function useAuthRequest(args: AuthArgs): useDataReturn {
	const { action, paths = {}, ...rest } = args;
	const { useRestRequest } = useDataRequest(rest);
	const { set, get, forget } = useAuthToken();
	const authPaths = {
		...AUTH_PATHS,
		...paths,
	};
	let params: RestArgs;
	switch (action) {
		case 'login':
			params = {
				path: authPaths.tokens,
				onData: (data, args) => {
					if (data && data.token) {
						const { token, refreshToken } = data;
						set({
							token,
							refreshToken,
						});
						if (args.onSuccess) {
							args.onSuccess(data);
						}
					}
				},
				headers: {
					preventRefreshToken: true,
				},
			};
			break;
		case 'refreshToken':
			params = {
				path: authPaths.refreshToken,
				data: {
					token: storage.get('token') || '',
					refreshToken: storage.get('refreshToken') || '',
				},
			};
			break;
		case 'forgot-pw':
			params = {
				path: authPaths.forgotPassword,
				onData: (data, args) => {
					if (args.onSuccess) {
						args.onSuccess(data);
					}
				},
			};
			break;
		case 'reset-pw':
			params = {
				path: authPaths.resetPassword,
				onData: (data, args) => {
					if (args.onSuccess) {
						args.onSuccess(data);
					}
				},
			};
			break;
		case 'logout':
			params = {
				path: authPaths.logout,
				jwt: true,
				method: 'POST',
				onSuccess: () => {
					forget();
				},
			};
			break;
		case 'change-pw':
			params = {
				path: authPaths.changePassword,
				method: 'PUT',
				jwt: true,
			};
			break;
	}

	return useRestRequest(params);
}
